import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const FIVE_CROWNS_ROUNDS = ['3s', '4s', '5s', '6s', '7s', '8s', '9s', '10s', 'Js', 'Qs', 'Ks'];
const PHASE_10_PHASES = [
  '2 sets of 3',
  '1 set of 3 + 1 run of 4',
  '1 set of 4 + 1 run of 4',
  '1 run of 7',
  '1 run of 8',
  '1 run of 9',
  '2 sets of 4',
  '7 cards of one color',
  '1 set of 5 + 1 set of 2',
  '1 set of 5 + 1 set of 3'
];

export default function GameHistory() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSession();
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const res = await fetch(`${window.API_URL}/api/scores/session/${sessionId}`);
      if (!res.ok) {
        throw new Error('Session not found');
      }
      const data = await res.json();
      setSession(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 flex items-center justify-center">
        <p className="text-white">Loading...</p>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 flex flex-col items-center justify-center p-4">
        <p className="text-red-400 mb-4">{error || 'Game not found'}</p>
        <button
          onClick={() => navigate('/')}
          className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg"
        >
          Back to Home
        </button>
      </div>
    );
  }

  const isFiveCrowns = session.slug === 'five-crowns';
  const isPhase10 = session.slug === 'phase-10';
  const rounds = isFiveCrowns ? FIVE_CROWNS_ROUNDS :
                 isPhase10 ? Array.from({ length: 10 }, (_, i) => i + 1) : [];

  // Get round scores from players if available
  const hasRoundScores = session.players?.some(p => p.round_scores && p.round_scores.length > 0);

  // Calculate phase progression for Phase 10
  const getPhaseProgression = (roundScores) => {
    if (!roundScores) return [];
    const progression = [];
    let currentPhase = 1;
    for (let i = 0; i < roundScores.length; i++) {
      const score = parseInt(roundScores[i]);
      if (!isNaN(score) && score < 50) {
        currentPhase = Math.min(11, currentPhase + 1);
      }
      progression.push(currentPhase);
    }
    return progression;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-white p-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{session.emoji || '🎮'}</span>
              <div>
                <h1 className="text-2xl font-bold text-white">{session.game_name}</h1>
                <p className="text-slate-400 text-sm">{formatDate(session.played_at)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Winner Banner */}
        {session.players?.[0] && (
          <div className={`rounded-xl p-6 mb-6 ${
            isFiveCrowns ? 'bg-gradient-to-r from-emerald-600/30 to-emerald-800/30 border border-emerald-500/30' :
            isPhase10 ? 'bg-gradient-to-r from-cyan-600/30 to-cyan-800/30 border border-cyan-500/30' :
            'bg-gradient-to-r from-purple-600/30 to-purple-800/30 border border-purple-500/30'
          }`}>
            <div className="flex items-center gap-4">
              <div className="text-5xl">🏆</div>
              <div>
                <p className="text-slate-300 text-sm uppercase tracking-wider">Winner</p>
                <p className="text-2xl font-bold text-white">{session.players[0].name}</p>
                <p className="text-lg text-yellow-400">{session.players[0].score} points</p>
              </div>
            </div>
          </div>
        )}

        {/* Final Standings */}
        <div className="bg-slate-800/50 rounded-xl overflow-hidden mb-6">
          <div className="p-4 border-b border-white/10">
            <h2 className="font-semibold text-white">Final Standings</h2>
          </div>
          <div className="divide-y divide-white/5">
            {session.players?.map((player, idx) => (
              <div key={player.user_id} className="p-4 flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  idx === 0 ? 'bg-yellow-500 text-black' :
                  idx === 1 ? 'bg-slate-400 text-black' :
                  idx === 2 ? 'bg-amber-700 text-white' :
                  'bg-slate-700 text-white'
                }`}>
                  {idx + 1}
                </div>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                  style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                >
                  {player.name?.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">{player.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-white">{player.score}</p>
                  <p className="text-xs text-slate-400">points</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Round-by-Round Scores */}
        {hasRoundScores && (
          <div
            onClick={() => navigate(`/history/${sessionId}/edit`)}
            className={`rounded-xl overflow-hidden cursor-pointer transition-all hover:ring-2 ${
              isFiveCrowns ? 'bg-emerald-950 border border-emerald-700/50 hover:ring-emerald-500' :
              isPhase10 ? 'bg-blue-950 border border-blue-700/50 hover:ring-blue-500' :
              'bg-slate-800/50 hover:ring-slate-500'
            }`}
          >
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="font-semibold text-white">Round-by-Round Scores</h2>
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <span>Tap to view/edit</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={isFiveCrowns ? 'bg-emerald-900' : isPhase10 ? 'bg-blue-800' : 'bg-slate-700'}>
                    <th className="p-3 text-left text-amber-400 font-bold">
                      {isFiveCrowns ? 'Wild' : 'Rnd'}
                    </th>
                    {session.players?.map((player) => (
                      <th key={player.user_id} className="p-3 text-center text-white font-medium min-w-[80px]">
                        {player.name?.split(' ')[0]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rounds.map((round, roundIdx) => {
                    const hasScoresForRound = session.players?.some(p =>
                      p.round_scores && p.round_scores[roundIdx] !== undefined && p.round_scores[roundIdx] !== ''
                    );
                    if (!hasScoresForRound) return null;

                    return (
                      <tr key={roundIdx} className="border-t border-white/5">
                        <td className={`p-3 font-medium ${isFiveCrowns ? 'text-amber-400' : 'text-cyan-300'}`}>
                          {round}
                        </td>
                        {session.players?.map((player) => {
                          const score = player.round_scores?.[roundIdx];
                          const isZero = score === 0 || score === '0';
                          return (
                            <td key={player.user_id} className={`p-3 text-center ${
                              isZero ? 'text-green-400 font-bold' : 'text-white'
                            }`}>
                              {score !== undefined && score !== '' ? score : '-'}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  {/* Totals Row */}
                  <tr className={`font-bold ${
                    isFiveCrowns ? 'bg-emerald-900' : isPhase10 ? 'bg-blue-800' : 'bg-slate-700'
                  }`}>
                    <td className="p-3 text-white">Total</td>
                    {session.players?.map((player) => (
                      <td key={player.user_id} className="p-3 text-center text-white">
                        {player.score}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Phase 10 Phase Progress Chart */}
        {isPhase10 && hasRoundScores && (
          <div className="bg-cyan-950 border border-cyan-700/50 rounded-xl overflow-hidden mt-6">
            <div className="p-4 border-b border-white/10">
              <h2 className="font-semibold text-white">📊 Phase Progress by Round</h2>
              <p className="text-cyan-300 text-xs mt-1">Shows what phase each player was on after each round</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-cyan-900">
                    <th className="p-3 text-left text-cyan-300 font-bold">After Round</th>
                    {session.players?.map((player) => (
                      <th key={player.user_id} className="p-3 text-center text-white font-medium min-w-[80px]">
                        {player.name?.split(' ')[0]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rounds.map((round, roundIdx) => {
                    const hasScoresForRound = session.players?.some(p =>
                      p.round_scores && p.round_scores[roundIdx] !== undefined && p.round_scores[roundIdx] !== ''
                    );
                    if (!hasScoresForRound) return null;

                    // Find leader(s) for this round
                    const progressions = session.players?.map(p => {
                      const prog = getPhaseProgression(p.round_scores);
                      return { userId: p.user_id, phase: prog[roundIdx] || 1 };
                    }) || [];
                    const maxPhase = Math.max(...progressions.map(p => p.phase));
                    const leaders = progressions.filter(p => p.phase === maxPhase).map(p => p.userId);

                    return (
                      <tr key={roundIdx} className="border-t border-white/5">
                        <td className="p-3 font-medium text-cyan-300">{round}</td>
                        {session.players?.map((player) => {
                          const progression = getPhaseProgression(player.round_scores);
                          const phase = progression[roundIdx] || 1;
                          const completedGame = phase > 10;
                          const isLeader = leaders.includes(player.user_id);
                          return (
                            <td key={player.user_id} className={`p-3 text-center font-mono ${
                              completedGame ? 'text-yellow-400 font-bold' :
                              isLeader ? 'text-cyan-300 font-bold' : 'text-white'
                            }`}>
                              {completedGame ? '🏆' : `P${phase}`}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-lg font-medium"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
